import { useEffect, useRef, useState } from 'react';
import PQueue from 'p-queue';

import useIsMounted from './useIsMounted';

const DEFAULT_BATCH = Object.freeze({
    complete:       false,
    completedCount: 0,
    errors:         [],
    pending:        false,
    pendingCount:   0,
    progress:       0,
    results:        [],
    size:           0,
    successful:     true,
    taskCount:      0
});

/**
 * This hook accepts a list of tasks and performs them in a rate-limited manner.
 * This is useful when performing many network requests that would otherwise
 * saturate the allowed network connections.
 *
 * Tasks can be added in an ad-hoc manner:
 *
 *     const [queue, runQueue] = useQueue()
 *     queue.add(async (fulfill, reject) => {
 *       const result = saveWidget(widget)
 *       fulfill({
 *         item: widget,
 *         value: result
 *       });
 *     });
 *
 * Or they can be added all at once with a single pre-defined processor:
 *
 *     const [queue, runQueue] = useQueue(async (item, ({ fulfill, reject }) =>
 *       {
 *         try {
 *           const result = saveWidget(item)
 *           fulfill({
 *             item,
 *             value: result
 *           })
 *         } catch (error) {
 *           reject({
 *             error,
 *             item,
 *           })
 *         }
 *       })
 *     )
 *     runQueue(items)
 *
 * The `queue` object contains the following pieces of state:
 *
 * `complete` - TRUE or FALSE if the queue has completed.
 * `completedCount` - Number of completed queued tasks.
 * `errors` - Array of any tasks that resulted in an error.
 * `pending` - TRUE or FALSE if the queue has tasks that have yet to resolve.
 * `pendingCount` - Number of currently executing tasks.
 * `progress` - Percentage of tasks that have completed.
 * `results` - Array of task results provided by the `fulfill` and `reject`
 * functions.
 * `size` - Number of non-completed tasks currently in the queue.
 * `successful` - TRUE or FALSE if all tasks completed successfully.
 * `taskCount` - Number of tasks that were added to the queue.
 */
export default function useQueue(processor) {
    const isMounted = useIsMounted();
    const pQueue = useRef(new PQueue({ concurrency: 4 }));
    const taskCount = useRef(0);
    const [queue, setStatus] = useState({
        add: enqueueTask,
        ...DEFAULT_BATCH
    });

    useEffect(() => {
        pQueue.current.on('active', handleActivity);
    }, []);

    /**
     * Responds to queue activity by tracking pending and completed tasks
     * in the queue status.
     */
    function handleActivity() {
        setStatus(({
            pendingCount,
            ...prevBatch
        }) => {
            const size = pQueue.current.size;
            const completedCount = taskCount.current - size;
            return {
                ...prevBatch,
                completedCount,
                pendingCount: pQueue.current.pending,
                progress:     completedCount / taskCount.current,
                size,
                taskCount:    taskCount.current
            };
        });
    }

    /**
     * Adds the specified task to the queue.
     */
    function enqueueTask(task) {
        pQueue.current.add(() => task(fulfillResult, rejectResult));
        taskCount.current = taskCount.current + 1;
        setStatus((prevBatch) => ({
            ...prevBatch,
            taskCount: taskCount.current
        }));
    }

    /**
     * Mark the batch item as fulfilled. Useful for post-processing once the
     * batch has completed.
     */
    function fulfillResult(result) {
        if (!isMounted.current) {
            return;
        }
        setStatus((prevBatch) => ({
            ...prevBatch,
            results: [
                ...prevBatch.results,
                {
                    ...result,
                    status: 'fulfilled'
                }
            ]
        }));
    }

    /**
     * Mark the batch item as rejected. Useful for error handling once the
     * batch has completed.
     */
    function rejectResult(result) {
        setStatus((prevBatch) => ({
            ...prevBatch,
            results: [
                ...prevBatch.results,
                {
                    ...result,
                    status: 'rejected'
                }
            ]
        }));
    }

    /**
     * Adds the provided items into the queue and waits for the batch to finish
     * processing, tracking its progress along the way.
     */
    async function runQueue(items = []) {
        setStatus((prevBatch) => ({
            ...prevBatch,
            complete: false,
            pending:  true
        }));
        items.forEach(item => {
            pQueue.current.add(
                () => processor(
                    item,
                    {
                        fulfill: fulfillResult,
                        reject:  rejectResult
                    }
                )
            );
            taskCount.current = taskCount.current + 1;
            setStatus((prevBatch) => ({
                ...prevBatch,
                taskCount: taskCount.current
            }));
        });
        await pQueue.current.onIdle();
        if (!isMounted.current) {
            return;
        }
        setStatus(({ results, ...prevBatch }) => {
            const errors = results.filter(
                ({ status }) => status === 'rejected'
            );
            return {
                ...prevBatch,
                complete:     true,
                errors,
                pending:      false,
                pendingCount: 0,
                progress:     1.0,
                results,
                size:         0,
                successful:   errors.length === 0,
                taskCount:    0
            };
        });
        taskCount.current = 0;
    }

    return [queue, runQueue];
}
